import {
  Content,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import clsx from 'clsx'
import React, { useMemo } from 'react'

import { DropdownMenuContext } from './context'
import { DropdownMenuItem } from './Item'
import { DropdownMenuSection } from './Section'

type TriggerProps = { open?: boolean; }

export interface DropdownMenuProps extends Omit<React.HTMLAttributes<HTMLElement>, 'className'> {
  align?: 'start' | 'center' | 'end';
  children: React.ReactNode;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  portalContainer?: Element | null;
  trigger: React.ReactNode | (({ open }: TriggerProps) => React.ReactNode);
  width?: 'auto' | 'triggerWidth';
  withPortal?: boolean;
  zIndex?: number;
}

function MaybePortal ({
  withPortal,
  portalContainer,
  children,
}: {
  withPortal: boolean;
  portalContainer?: Element | null;
  children: React.ReactNode;
}) {
  if (withPortal) {
    return <Portal container={portalContainer}>{children}</Portal>
  }
  return children
};

export function DropdownMenu ({
  align = 'start',
  children,
  defaultOpen = false,
  onOpenChange,
  open: openProp,
  portalContainer,
  trigger,
  width = 'auto',
  withPortal = true,
  zIndex,
  ...props
}: DropdownMenuProps) {
  const [open, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  })

  function renderTrigger () {
    if (typeof trigger === 'function') {
      return trigger({ open })
    }

    return trigger
  }

  const contextValue = useMemo(
    () => ({ closeMenu: () => setOpen(false) }),
    [setOpen]
  )

  return (
    <Root
      defaultOpen={defaultOpen}
      open={open}
      onOpenChange={(bool) => {
        setOpen(bool)
      }}
    >
      <Trigger {...props} asChild>{renderTrigger()}</Trigger>
      <MaybePortal portalContainer={portalContainer} withPortal={withPortal}>
        <Content
          align={align}
          className={clsx(
            'rounded-lg py-1.5 border border-gray-200 bg-white shadow data-[side=top]:animate-slide-up-and-fade-in data-[side=bottom]:animate-slide-down-and-fade-in min-w-[--radix-dropdown-menu-trigger-width]',
            {
              'w-[--radix-dropdown-menu-trigger-width]': width === 'triggerWidth',
            }
          )}
          sideOffset={4}
          style={{ zIndex }}
        >
          <DropdownMenuContext.Provider value={contextValue}>
            {children}
          </DropdownMenuContext.Provider>
        </Content>
      </MaybePortal>
    </Root>
  )
}

DropdownMenu.Item = DropdownMenuItem
DropdownMenu.Section = DropdownMenuSection
